import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Award } from "lucide-react";

import { AdminAPI } from "@/lib/admin-api";
import { Card, CardBody } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Table, THead, TR, TH, TD } from "@/components/ui/Table";
import { StatusPill } from "@/components/ui/StatusPill";
import { Badge } from "@/components/ui/Badge";
import { Skeleton } from "@/components/ui/Skeleton";
import { EmptyState } from "@/components/ui/EmptyState";
import { RewardRuleBuilder } from "./RewardRuleBuilder";

export function RewardRulesPage() {
  const [open, setOpen] = useState(false);
  const qc = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["admin-reward-rules"],
    queryFn: AdminAPI.listRewardRules,
  });

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-surface-900">Reward Rules</h1>
          <p className="text-sm text-surface-500 mt-1">
            Versioned reward formulas. Historical rewards are pinned to the version applied at award time.
          </p>
        </div>
        <Button leftIcon={<Plus className="h-4 w-4" />} onClick={() => setOpen(true)}>
          New rule
        </Button>
      </div>

      <Card>
        <CardBody className="p-0">
          {isLoading ? (
            <div className="p-5 space-y-3">
              {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-14" />)}
            </div>
          ) : data && data.length ? (
            <Table>
              <THead>
                <TR>
                  <TH>Name</TH><TH>Trigger</TH><TH>Reward</TH>
                  <TH>Formula</TH><TH>Version</TH><TH>Stage</TH><TH>Status</TH>
                </TR>
              </THead>
              <tbody>
                {data.map((r) => (
                  <TR key={r.id}>
                    <TD className="font-medium">{r.name}</TD>
                    <TD><Badge tone="info" className="capitalize">{r.trigger_type.replace(/_/g, " ")}</Badge></TD>
                    <TD className="capitalize">{r.reward_type}</TD>
                    <TD className="capitalize">{r.current_version?.formula_type ?? "—"}</TD>
                    <TD>v{r.current_version?.version_number ?? 1}</TD>
                    <TD>{r.applies_to_stage_number ?? "All"}</TD>
                    <TD>
                      <StatusPill status={r.is_active ? "active" : "inactive"} />
                    </TD>
                  </TR>
                ))}
              </tbody>
            </Table>
          ) : (
            <EmptyState icon={Award} title="No reward rules yet"
                        description="Create your first rule to enable automated reward payouts." />
          )}
        </CardBody>
      </Card>

      {open && (
        <RewardRuleBuilder
          open onClose={() => setOpen(false)}
          onSaved={() => {
            qc.invalidateQueries({ queryKey: ["admin-reward-rules"] });
            setOpen(false);
          }}
        />
      )}
    </div>
  );
}
